import { useEffect, useState } from 'react';
import { requestAndSaveNotificationToken } from '../services/notificationService';

export default function EnableNotificationsButton({ compact = false, onDone }) {
  const getCurrentPermission = () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return 'unsupported';
    }

    return Notification.permission;
  };

  const [permission, setPermission] = useState(getCurrentPermission);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const isGranted = permission === 'granted';
  const isDenied = permission === 'denied';
  const isUnsupported = permission === 'unsupported';
  const isDefault = permission === 'default';

  useEffect(() => {
    if (!('Notification' in window)) {
      setPermission('unsupported');
      setStatus('Notifiche non supportate');
      return;
    }

    setPermission(Notification.permission);

    if (Notification.permission === 'granted') {
      setStatus('Notifiche attive');
    }
  }, []);

  async function handleEnableNotifications() {
    try {
      setLoading(true);
      setStatus('Attivazione notifiche...');

      await requestAndSaveNotificationToken();

      setPermission(Notification.permission);
      setStatus('Notifiche attive');

      if (typeof onDone === 'function') {
        onDone();
      }
    } catch (error) {
      if ('Notification' in window) {
        setPermission(Notification.permission);
      }

      setStatus(error.message || 'Errore durante attivazione notifiche.');
    } finally {
      setLoading(false);
    }
  }

  if (compact) {
    if (isUnsupported) {
      return (
        <div
          title="Le notifiche non sono supportate da questo browser."
          className="inline-flex items-center gap-2 rounded-lg border border-slate-500/30 bg-slate-500/10 px-3 py-2 text-sm font-semibold text-slate-300"
        >
          <span>🔕</span>
          <span>Non supportate</span>
        </div>
      );
    }

    if (isGranted) {
      return (
        <button
          type="button"
          onClick={handleEnableNotifications}
          disabled={loading}
          title="Le notifiche sono attive. Clicca per risincronizzare questo dispositivo."
          className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span>🔔</span>
          <span>{loading ? 'Sincronizzo...' : 'Notifiche attive'}</span>
        </button>
      );
    }

    if (isDenied) {
      return (
        <div
          title="Le notifiche sono bloccate dal browser. Riattivale dalle impostazioni del sito."
          className="inline-flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm font-semibold text-amber-300"
        >
          <span>🔕</span>
          <span>Notifiche bloccate</span>
        </div>
      );
    }

    return (
      <button
        type="button"
        onClick={handleEnableNotifications}
        disabled={loading}
        className="inline-flex items-center gap-2 rounded-lg border border-blue-500/40 bg-blue-600/20 px-3 py-2 text-sm font-semibold text-blue-100 hover:bg-blue-600/35 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span>🔔</span>
        <span>{loading ? 'Attivazione...' : 'Attiva notifiche'}</span>
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-900">
        Notifiche allarmi
      </h3>

      <p className="mt-1 text-sm text-slate-600">
        Ricevi allarmi e comunicazioni anche quando la dashboard non è aperta.
      </p>

      {isUnsupported && (
        <div className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700">
          Notifiche non supportate da questo browser.
        </div>
      )}

      {isGranted && (
        <button
          type="button"
          onClick={handleEnableNotifications}
          disabled={loading}
          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span>🔔</span>
          <span>{loading ? 'Sincronizzo...' : 'Notifiche attive'}</span>
        </button>
      )}

      {isDenied && (
        <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800">
          Notifiche bloccate. Riattivale dalle impostazioni del browser.
        </div>
      )}

      {isDefault && (
        <button
          type="button"
          onClick={handleEnableNotifications}
          disabled={loading}
          className="mt-3 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'Attivazione...' : 'Attiva notifiche'}
        </button>
      )}

      {status && (
        <p className="mt-2 text-sm text-slate-700">
          {status}
        </p>
      )}
    </div>
  );
}